// client/src/coms/views/canvas_parts/canvas_room_view_parts/brushImport/safePlist.js
//
// Binary and XML property-list readers for untrusted brush files. Every
// offset is bounds-checked, nesting depth and object counts are capped, and
// NSKeyedArchiver references are resolved with cycle protection. The XML
// reader is a small tokenizer: no DTDs, no entities beyond the five XML
// builtins, so external-entity and billion-laughs payloads have no effect.

const MAX_OBJECTS = 200000;
const MAX_DEPTH = 64;
const MAX_CONTAINER_LENGTH = 100000;

export class PlistError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PlistError';
  }
}

function readUInt(bytes, offset, length) {
  if (offset < 0 || offset + length > bytes.length) throw new PlistError('Plist read past end of data');
  let value = 0;
  for (let i = 0; i < length; i += 1) value = value * 256 + bytes[offset + i];
  return value;
}

export function isBinaryPlist(bytes) {
  if (!bytes || bytes.length < 8) return false;
  return String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3], bytes[4], bytes[5]) === 'bplist';
}

export function parseBinaryPlist(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || new ArrayBuffer(0));
  if (bytes.length < 40 || !isBinaryPlist(bytes)) throw new PlistError('Not a binary plist');

  const trailer = bytes.length - 32;
  const offsetSize = bytes[trailer + 6];
  const refSize = bytes[trailer + 7];
  const objectCount = readUInt(bytes, trailer + 8, 8);
  const topObject = readUInt(bytes, trailer + 16, 8);
  const tableOffset = readUInt(bytes, trailer + 24, 8);
  if (!offsetSize || offsetSize > 8 || !refSize || refSize > 8) throw new PlistError('Invalid plist trailer');
  if (objectCount > MAX_OBJECTS) throw new PlistError('Plist has too many objects');
  if (tableOffset + objectCount * offsetSize > trailer) throw new PlistError('Plist offset table out of range');
  if (topObject >= objectCount) throw new PlistError('Plist top object out of range');

  const offsets = new Array(objectCount);
  for (let i = 0; i < objectCount; i += 1) {
    const value = readUInt(bytes, tableOffset + i * offsetSize, offsetSize);
    if (value >= tableOffset) throw new PlistError('Plist object offset out of range');
    offsets[i] = value;
  }

  const cache = new Map();
  const active = new Set();

  function lengthAt(offset, info) {
    if (info !== 0x0f) return { length: info, start: offset + 1 };
    const marker = bytes[offset + 1];
    if ((marker >> 4) !== 0x1) throw new PlistError('Invalid plist length marker');
    const size = 1 << (marker & 0x0f);
    if (size > 8) throw new PlistError('Invalid plist length size');
    const length = readUInt(bytes, offset + 2, size);
    return { length, start: offset + 2 + size };
  }

  function parse(index, depth) {
    if (index >= objectCount) throw new PlistError('Plist reference out of range');
    if (cache.has(index)) return cache.get(index);
    if (depth > MAX_DEPTH) throw new PlistError('Plist nesting too deep');
    if (active.has(index)) return null;
    active.add(index);
    const offset = offsets[index];
    const marker = bytes[offset];
    const type = marker >> 4;
    const info = marker & 0x0f;
    let value = null;

    if (type === 0x0) {
      value = info === 0x8 ? false : info === 0x9 ? true : null;
    } else if (type === 0x1) {
      const size = 1 << info;
      if (size > 8) throw new PlistError('Invalid plist integer');
      if (size === 8) {
        const high = readUInt(bytes, offset + 1, 4);
        const low = readUInt(bytes, offset + 5, 4);
        value = high >= 0x80000000 ? -((0xffffffff - high) * 4294967296 + (0x100000000 - low)) : high * 4294967296 + low;
      } else {
        value = readUInt(bytes, offset + 1, size);
      }
    } else if (type === 0x2) {
      const size = 1 << info;
      if (offset + 1 + size > bytes.length) throw new PlistError('Plist real out of range');
      const view = new DataView(bytes.buffer, bytes.byteOffset + offset + 1, size);
      value = size === 4 ? view.getFloat32(0, false) : size === 8 ? view.getFloat64(0, false) : 0;
    } else if (type === 0x3) {
      if (offset + 9 > bytes.length) throw new PlistError('Plist date out of range');
      const view = new DataView(bytes.buffer, bytes.byteOffset + offset + 1, 8);
      value = new Date((view.getFloat64(0, false) + 978307200) * 1000);
    } else if (type === 0x4 || type === 0x5 || type === 0x6) {
      const { length, start } = lengthAt(offset, info);
      const byteLength = type === 0x6 ? length * 2 : length;
      if (start + byteLength > bytes.length) throw new PlistError('Plist string out of range');
      if (type === 0x4) {
        value = bytes.slice(start, start + byteLength);
      } else if (type === 0x5) {
        value = '';
        for (let i = 0; i < length; i += 1) value += String.fromCharCode(bytes[start + i]);
      } else {
        value = '';
        for (let i = 0; i < length; i += 1) value += String.fromCharCode(readUInt(bytes, start + i * 2, 2));
      }
    } else if (type === 0x8) {
      value = { __uid: readUInt(bytes, offset + 1, info + 1) };
    } else if (type === 0xa || type === 0xc || type === 0xd) {
      const { length, start } = lengthAt(offset, info);
      if (length > MAX_CONTAINER_LENGTH) throw new PlistError('Plist container too large');
      if (type === 0xd) {
        value = {};
        for (let i = 0; i < length; i += 1) {
          const key = parse(readUInt(bytes, start + i * refSize, refSize), depth + 1);
          const item = parse(readUInt(bytes, start + (length + i) * refSize, refSize), depth + 1);
          const safeKey = String(key);
          if (safeKey === '__proto__' || safeKey === 'constructor' || safeKey === 'prototype') continue;
          value[safeKey] = item;
        }
      } else {
        value = [];
        for (let i = 0; i < length; i += 1) value.push(parse(readUInt(bytes, start + i * refSize, refSize), depth + 1));
      }
    } else {
      value = null;
    }

    active.delete(index);
    cache.set(index, value);
    return value;
  }

  return parse(topObject, 0);
}

// Resolves an NSKeyedArchiver plist to plain values. Class wrappers such as
// NSDictionary/NSArray/NSString are flattened; other objects keep their keys
// with a `$class` name so translators can tell, for example, a curve object
// apart from a plain number.
export function unarchiveKeyedPlist(root) {
  if (!root || typeof root !== 'object' || !Array.isArray(root.$objects)) return root;
  const objects = root.$objects;
  const memo = new Map();

  function className(object) {
    const ref = object && object.$class;
    const refIndex = ref && typeof ref === 'object' ? ('__uid' in ref ? ref.__uid : ref['CF$UID']) : undefined;
    const cls = refIndex != null ? objects[refIndex] : null;
    return cls && cls.$classname ? String(cls.$classname) : '';
  }

  function resolve(value, depth) {
    if (depth > MAX_DEPTH) return null;
    // Binary plists carry UID objects; XML keyed archives use {CF$UID: n}.
    const uid = value && typeof value === 'object' && Object.keys(value).length === 1
      ? ('__uid' in value ? value.__uid : 'CF$UID' in value ? value['CF$UID'] : null)
      : null;
    if (uid != null) {
      const index = uid;
      if (memo.has(index)) return memo.get(index);
      memo.set(index, null);
      const result = resolve(objects[index], depth + 1);
      memo.set(index, result);
      return result;
    }
    if (value === '$null') return null;
    if (Array.isArray(value)) return value.map((item) => resolve(item, depth + 1));
    if (!value || typeof value !== 'object' || value instanceof Uint8Array || value instanceof Date) return value;

    const cls = className(value);
    if (cls === 'NSString' || cls === 'NSMutableString') return resolve(value['NS.string'], depth + 1);
    if (cls === 'NSData' || cls === 'NSMutableData') return resolve(value['NS.data'], depth + 1);
    if (Array.isArray(value['NS.keys']) && Array.isArray(value['NS.objects'])) {
      const out = {};
      value['NS.keys'].forEach((key, i) => {
        const name = String(resolve(key, depth + 1));
        if (name === '__proto__' || name === 'constructor' || name === 'prototype') return;
        out[name] = resolve(value['NS.objects'][i], depth + 1);
      });
      return out;
    }
    if (Array.isArray(value['NS.objects'])) return value['NS.objects'].map((item) => resolve(item, depth + 1));

    const out = {};
    Object.keys(value).forEach((key) => {
      if (key === '$class') return;
      out[key] = resolve(value[key], depth + 1);
    });
    if (cls) out.$class = cls;
    return out;
  }

  const top = root.$top && typeof root.$top === 'object' ? root.$top : {};
  const rootRef = top.root !== undefined ? top.root : Object.values(top)[0];
  return resolve(rootRef, 0);
}

function decodeXmlText(text) {
  return String(text || '')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (m, code) => {
      const n = Number(code);
      return n > 0 && n < 0x110000 ? String.fromCodePoint(n) : '';
    })
    .replace(/&#x([0-9a-f]+);/gi, (m, code) => {
      const n = parseInt(code, 16);
      return n > 0 && n < 0x110000 ? String.fromCodePoint(n) : '';
    })
    .replace(/&amp;/g, '&');
}

export function parseXmlPlist(text) {
  const source = String(text || '');
  if (source.length > 8 * 1024 * 1024) throw new PlistError('XML plist too large');
  const tokens = [];
  const regex = /<(\/?)([a-zA-Z]+)(?:\s[^>]*?)?\s*(\/?)>|([^<]+)/g;
  // DOCTYPEs (including internal subsets with entity declarations) are
  // dropped unread; only the five builtin entities are ever decoded.
  const body = source
    .replace(/<\?[\s\S]*?\?>/g, '')
    .replace(/<!DOCTYPE[^[>]*(\[[\s\S]*?\])?\s*>/gi, '')
    .replace(/<!--[\s\S]*?-->/g, '');
  let match;
  while ((match = regex.exec(body))) {
    if (match[4] != null) {
      if (match[4].trim()) tokens.push({ text: match[4] });
    } else {
      tokens.push({ close: match[1] === '/', tag: match[2], selfClose: match[3] === '/' });
    }
  }
  let pos = 0;

  function expectClose(tag) {
    const token = tokens[pos];
    if (!token || !token.close || token.tag !== tag) throw new PlistError('Malformed XML plist near ' + tag);
    pos += 1;
  }

  function readText(tag) {
    let text = '';
    if (tokens[pos] && tokens[pos].text != null) {
      text = tokens[pos].text;
      pos += 1;
    }
    expectClose(tag);
    return decodeXmlText(text);
  }

  function value(depth) {
    if (depth > MAX_DEPTH) throw new PlistError('XML plist nesting too deep');
    const token = tokens[pos];
    if (!token || token.close || token.text != null) throw new PlistError('Malformed XML plist');
    pos += 1;
    const tag = token.tag;
    if (token.selfClose) {
      if (tag === 'true') return true;
      if (tag === 'false') return false;
      if (tag === 'dict') return {};
      if (tag === 'array') return [];
      if (tag === 'string') return '';
      return null;
    }
    if (tag === 'plist') {
      const inner = value(depth + 1);
      expectClose('plist');
      return inner;
    }
    if (tag === 'dict') {
      const out = {};
      while (tokens[pos] && !(tokens[pos].close && tokens[pos].tag === 'dict')) {
        const keyToken = tokens[pos];
        if (!keyToken || keyToken.tag !== 'key' || keyToken.close) throw new PlistError('XML plist dict without key');
        pos += 1;
        const key = readText('key');
        const item = value(depth + 1);
        if (key !== '__proto__' && key !== 'constructor' && key !== 'prototype') out[key] = item;
      }
      expectClose('dict');
      return out;
    }
    if (tag === 'array') {
      const out = [];
      while (tokens[pos] && !(tokens[pos].close && tokens[pos].tag === 'array')) {
        if (out.length > MAX_CONTAINER_LENGTH) throw new PlistError('XML plist array too large');
        out.push(value(depth + 1));
      }
      expectClose('array');
      return out;
    }
    const text = readText(tag);
    if (tag === 'string') return text;
    if (tag === 'integer') return parseInt(text, 10) || 0;
    if (tag === 'real') return Number(text) || 0;
    if (tag === 'true') return true;
    if (tag === 'false') return false;
    if (tag === 'date') return new Date(text);
    return text;
  }

  return value(0);
}

export function parseAnyPlist(input) {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input || new ArrayBuffer(0));
  if (isBinaryPlist(bytes)) return parseBinaryPlist(bytes);
  const text = typeof TextDecoder !== 'undefined' ? new TextDecoder('utf-8').decode(bytes) : '';
  return parseXmlPlist(text);
}
